import React from 'react'

import { isStandardMetric, TCustomizedMetric } from 'types/GlobalMetric'

import { useMetric } from 'redux/hooks/Metrics'
import { useTranslation } from 'language/translator'

import Button from 'components_new/atoms/Button'
import Chip from 'components_new/atoms/Chip'
import Icon from 'components_new/atoms/Icon'
import Link from 'components_new/atoms/Link'
import Text from 'components_new/atoms/Text'

import SidebarDialog from 'components_new/molecules/SidebarDialog'
import SimpleTable from 'components_new/molecules/SimpleTable'

interface Props {
  metric: TCustomizedMetric | null
  onClose: () => void
  open: boolean
  title: string
}

const MetricInfoDialog = (props: Props) => {
  const { metric, onClose, open, title } = props

  const fullMetric = useMetric(
    metric?.id,
    metric?.settings.variant_kpi_template_id
  )
  const { lang, t, translateEnum } = useTranslation()

  if (!fullMetric || !metric) return null

  const empty = (
    <Text color="text.disabled" fontStyle="italic" variant="body2">
      {t('global', 'none')}
    </Text>
  )

  /*-- distribution --*/
  const getDistribution = (variant: 'chip' | 'text') => {
    const distribution = metric.settings?.distribution

    if (variant === 'chip') {
      if (!distribution) return empty

      return (
        <Chip
          color="default"
          label={translateEnum('show_pers', distribution).abbreviation}
          size="xsmall"
        />
      )
    }

    if (!distribution) return ''

    return (
      <Text color="text.secondary" variant="body2">
        {translateEnum('show_pers', distribution).helperText}
      </Text>
    )
  }

  const getRollingSum = (variant: 'chip' | 'text') => {
    const rollingSum = metric.settings?.rolling_sum

    if (variant === 'chip') {
      if (!rollingSum) return empty

      return (
        <Chip
          color="default"
          label={translateEnum('rolling_types', rollingSum).abbreviation}
          size="xsmall"
        />
      )
    }

    if (!rollingSum) return ''

    return (
      <Text color="text.secondary" variant="body2">
        {translateEnum('rolling_types', rollingSum).helperText}
      </Text>
    )
  }

  const getSlices = () => {
    const slices = metric.settings.slices

    if (!slices || slices.length === 0) return empty

    return fullMetric.slices.all
      .filter((s) => slices.includes(s.id))
      .map((slice, i) => (
        <Chip color="default" key={i} label={slice.name} size="xsmall" />
      ))
  }

  return (
    <>
      <SidebarDialog
        actions={
          <>
            <Button variant="text" onClick={onClose}>
              {t('global', 'close')}
            </Button>
          </>
        }
        maxWidth="sm"
        onClose={onClose}
        open={open}
        title={`${t('global', 'more_information')}`}
      >
        <SimpleTable
          rows={[
            {
              cells: [
                t('global', 'title'),
                <Text key={1} noWrap={true} variant="body2">
                  {title}
                </Text>,
                ' '
              ]
            },
            {
              cells: [
                t('metrics', 'metric'),
                <Link
                  arrow={true}
                  href={`/metrics/${metric.id}`}
                  key={metric.id}
                >
                  <Icon
                    fontSize="inherit"
                    name="Metric"
                    sx={{ mr: '0.25em', verticalAlign: 'middle' }}
                  />
                  {isStandardMetric(fullMetric)
                    ? fullMetric.title[lang]
                    : fullMetric.full_title[lang]}
                </Link>,
                <Text
                  color="text.secondary"
                  key={'metric-summary'}
                  variant="body2"
                >
                  {isStandardMetric(fullMetric) ? fullMetric.summary[lang] : ''}
                </Text>
              ]
            },
            {
              cells: [
                t('metrics', 'rolling_sum'),
                getRollingSum('chip'),
                getRollingSum('text')
              ]
            },
            {
              cells: [
                t('metrics', 'distribution'),
                getDistribution('chip'),
                getDistribution('text')
              ]
            },
            { cells: [t('entities', 'slices'), getSlices(), ' '] }
          ]}
          sx={{ mt: 2 }}
        />
      </SidebarDialog>
    </>
  )
}

export default MetricInfoDialog
